/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Card, CardBody } from 'reactstrap';
import TemplateComponent from 'react-mustache-template-component';

import { ReactComponent as LabelTaskIcon } from 'assets/icon-label-task.svg';
import { ReactComponent as FingerIcon } from 'assets/icon-finger.svg';
import { ReactComponent as FacebookIcon } from 'assets/icon-facebook.svg';
import { ReactComponent as YoutubeIcon } from 'assets/icon-youtube.svg';
import { ReactComponent as TwitterIcon } from 'assets/icon-twitter.svg';
import { ReactComponent as LinkedinIcon } from 'assets/icon-linkedin.svg';
import { colors } from 'shared/defaultTheme';

import { PREVIEW_NOTIFICATION } from 'shared/constants/variants';

const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  height: auto;
  width: 100%;
  margin-bottom: 10px;
`;

const Logo = styled.img`
  width: unset;
  height: 70px;
`;

const FooterLogo = styled.img`
  width: unset;
  height: 60px;
`;

const ColorBar = styled.div`
  background-color: ${({ color }) => color};
  height: 5px;
  width: 100%;
`;

const ImagePreview = styled.div`
  background: url('${({ imageSrc }) => imageSrc}');
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  height: 275px;
  width: 100%;
`;

const BodyPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 30px;
  padding-right: 25px;
  margin-bottom: 68px;
  width: 100%;

  .icon-finger {
    align-self: center;
    fill: ${colors.black};
    height: 40px;
    width: 35px;
  }
`;

const ContainerBorderLeft = styled.div`
  height: 10px;
  padding: 15px 0 20px 9px;
  margin-bottom: 27px;
`;

const TitleNotificationWrapper = styled.div`
  display: flex;
  flex-direction: row;

  svg {
    fill: ${colors.black};
    height: 36px;
    width: 39px;
    margin-right: 11px;
  }

  span {
    align-self: center;
    color: ${colors.black};
    font-family: Lato;
    font-size: 18px;
    letter-spacing: 0;
    line-height: 18px;
  }
`;

const FooterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: ${({ flex }) => flex};
  align-items: ${({ direction }) => direction};
  padding-top: ${({ paddingTop }) => paddingTop};
  padding-left: 17px;
  padding-right: 2px;
`;

const PhoneText = styled.span`
  color: ${({ mainColor }) => mainColor};
  font-family: MTTMilano;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: 2.5px;
  line-height: 10px;
  text-align: center;
`;

const IconsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 7px;
  margin-left: 15px;
`;

const IconContainer = styled.div(({ color }) => ({
  width: '16px',
  height: '16px',
  backgroundColor: color,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: '16px',
  borderRadius: 50,
  cursor: 'pointer',
}));

const renderBodyText = (variant, t) => {
  switch (variant) {
    case PREVIEW_NOTIFICATION.CHAT:
      return (
        <p className="body-text">
          {t('notification_preview_notification_body_text_1')}
          <br />
          <br />
          {t('notification_preview_notification_body_text_2')}
          <br />
          <br />
          {t('notification_preview_notification_body_text_3')}
        </p>
      );
    case PREVIEW_NOTIFICATION.CUSTOMIZER:
      return (
        <p className="body-text">
          {t('general_customizer_preivew_notification_body_text_1')}
          <br />
          <br />
          {t('general_customizer_preivew_notification_body_text_2')}{' '}
          <b>{t('general_customizer_preivew_notification_body_text_3')}</b>{' '}
          {t('general_customizer_preivew_notification_body_text_4')}
        </p>
      );
    default:
      return null;
  }
};

const PreviewNotification = ({
  newName,
  variant,
  logoHeader,
  mainColor,
  logoFooter,
  footerText,
  phone,
  logoNotify,
  selectedPageFooterOption,
  theme,
}) => {
  const wrapperClass = {
    [PREVIEW_NOTIFICATION.CHAT]: '',
    [PREVIEW_NOTIFICATION.CUSTOMIZER]: 'general-customizer__preview-wrapper',
  };
  const cardClass = {
    [PREVIEW_NOTIFICATION.CHAT]: '',
    [PREVIEW_NOTIFICATION.CUSTOMIZER]: 'general-customizer__card-preview-notification',
  };
  const { t } = useTranslation();

  const TemplateComponentDiv = styled.div`
    color: ${theme.footerTextColor};
    font-family: 'MTTMilano';
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 10px;
    margin-top: 12px;
    padding-bottom: 15px;
    a {
      text-decoration: underline;
      color: ${theme.footerTextColor};
    }
  `;

  return (
    <div className={wrapperClass[variant]}>
      <Card className={cardClass[variant]}>
        <CardBody>
          <LogoContainer
            style={{
              backgroundColor: theme.backgroundHeader,
            }}
          >
            <Logo src={logoHeader} alt="header-logo" />
          </LogoContainer>
          <ColorBar color={mainColor} />
          <ImagePreview imageSrc={logoNotify} />
          <BodyPreviewContainer>
            <ContainerBorderLeft mainColor={mainColor} />
            <TitleNotificationWrapper>
              <LabelTaskIcon />
              <span>
                {newName !== '' ? newName : t('general_customizer_preview_notification_title')}
              </span>
            </TitleNotificationWrapper>
            {renderBodyText(variant, t)}
            <span className="link-text text-center">
              {t('general_customizer_preview_direct_access')}
            </span>
            <FingerIcon className="icon-finger" />
          </BodyPreviewContainer>
          <ColorBar color={mainColor} />
          <FooterWrapper>
            <FooterSection
              flex={0.7}
              direction="flex-start"
              paddingTop="14px"
              style={{
                backgroundColor: theme.backgroundFooter,
              }}
            >
              <FooterLogo src={logoFooter} alt="footer-logo" />
              <TemplateComponentDiv>
                <TemplateComponent template={footerText} />
              </TemplateComponentDiv>
            </FooterSection>
            <FooterSection
              flex={0.3}
              direction="center"
              paddingTop="34px"
              style={{ backgroundColor: theme.backgroundFooter }}
            >
              <PhoneText mainColor={theme.footerTextColor}>{phone}</PhoneText>
              <IconsWrapper>
                <IconContainer color={theme.footerIconColor}>
                  <FacebookIcon
                    fill={
                      selectedPageFooterOption === 1 || selectedPageFooterOption === 2
                        ? colors.white
                        : mainColor
                    }
                    height={10.5}
                  />
                </IconContainer>
                <IconContainer color={theme.footerIconColor}>
                  <LinkedinIcon
                    fill={
                      selectedPageFooterOption === 1 || selectedPageFooterOption === 2
                        ? colors.white
                        : mainColor
                    }
                    height={10.5}
                  />
                </IconContainer>
                <IconContainer color={theme.footerIconColor}>
                  <YoutubeIcon
                    fill={
                      selectedPageFooterOption === 1 || selectedPageFooterOption === 2
                        ? colors.white
                        : mainColor
                    }
                    height={10.5}
                  />
                </IconContainer>
                <IconContainer color={theme.footerIconColor}>
                  <TwitterIcon
                    fill={
                      selectedPageFooterOption === 1 || selectedPageFooterOption === 2
                        ? colors.white
                        : mainColor
                    }
                    height={11.5}
                  />
                </IconContainer>
              </IconsWrapper>
            </FooterSection>
          </FooterWrapper>
        </CardBody>
      </Card>
    </div>
  );
};

PreviewNotification.defaultProps = {
  newName: '',
};

PreviewNotification.propTypes = {
  newName: PropTypes.string,
  selectedPageFooterOption: PropTypes.number.isRequired,
  variant: PropTypes.string.isRequired,
  logoHeader: PropTypes.string.isRequired,
  mainColor: PropTypes.string.isRequired,
  logoFooter: PropTypes.string.isRequired,
  footerText: PropTypes.string.isRequired,
  phone: PropTypes.string.isRequired,
  logoNotify: PropTypes.string.isRequired,
};

export default PreviewNotification;
